/**
 * Numbered migrations for `_state.db`, applied after the baseline
 * (STATE_BASELINE_VERSION = 5). Append new steps at the end; never edit or
 * reorder a step that has shipped.
 */
import { addColumnIfMissing, type Migration, type SchemaDefinition } from '../migrations.ts'
import { DECK_STATS_VIEW, STATE_BASELINE_VERSION, STATE_DB_SCHEMA } from './state-schema.ts'

export const STATE_MIGRATIONS: readonly Migration[] = [
  {
    version: 6,
    description: 'Backfill legacy columns; recreate deck_stats with disjoint, timezone-correct buckets',
    up: async (db) => {
      // Columns that older builds added outside of any versioned step. The
      // baseline creates them for new DBs; very old DBs may still lack them.
      await addColumnIfMissing(db, 'deck_instances', 'card_style', 'TEXT')
      await addColumnIfMissing(db, 'deck_instances', 'deck_color', 'TEXT')
      await addColumnIfMissing(db, 'tags', 'parent_id', 'TEXT')

      await db.run('DROP VIEW IF EXISTS deck_stats')
      await db.run(DECK_STATS_VIEW)
    },
  },
  {
    version: 7,
    description: 'Replication: HLC updated_at on every replicated row, tombstones, unique review_log key',
    up: async (db) => {
      // '' = "written by a version that did not stamp rows" — oldest possible.
      for (const table of ['deck_instances', 'tags', 'deck_tags', 'sessions', 'session_decks', 'review_state']) {
        await addColumnIfMissing(db, table, 'updated_at', "TEXT NOT NULL DEFAULT ''")
      }

      await db.run(`
        CREATE TABLE IF NOT EXISTS tombstones (
          entity     TEXT NOT NULL,
          key        TEXT NOT NULL,
          deleted_at TEXT NOT NULL,
          PRIMARY KEY (entity, key)
        ) STRICT
      `)

      // review_log is a grow-only set keyed by what identifies a review. Older
      // merges could leave duplicates; keep the first copy, then enforce it.
      await db.run(`
        DELETE FROM review_log WHERE id NOT IN (
          SELECT MIN(id) FROM review_log
          GROUP BY card_id, deck_instance_id, reviewed_at, session_id
        )
      `)
      await db.run(`
        CREATE UNIQUE INDEX IF NOT EXISTS ux_review_log_natural
        ON review_log(card_id, deck_instance_id, reviewed_at, session_id)
      `)

      // tags.name loses its UNIQUE constraint: two devices may create the same
      // name offline, and both rows must be able to replicate. Reads collapse
      // same-name tags through the tag_canon view (oldest, then lowest id wins).
      // SQLite cannot drop a constraint, so the table is rebuilt.
      await db.run(`
        CREATE TABLE tags_v7 (
          id         TEXT    PRIMARY KEY,
          name       TEXT    NOT NULL,
          parent_id  TEXT,
          color      TEXT    NOT NULL,
          created_at INTEGER NOT NULL,
          updated_at TEXT    NOT NULL DEFAULT ''
        ) STRICT
      `)
      await db.run(`
        INSERT INTO tags_v7 (id, name, parent_id, color, created_at, updated_at)
        SELECT id, name, parent_id, color, created_at, updated_at FROM tags
      `)
      await db.run('DROP TABLE tags')
      await db.run('ALTER TABLE tags_v7 RENAME TO tags')
      await db.run('CREATE INDEX IF NOT EXISTS idx_tags_name ON tags(name)')
      await db.run(`
        CREATE VIEW IF NOT EXISTS tag_canon AS
        SELECT t.id AS tag_id,
               (SELECT t2.id FROM tags t2 WHERE t2.name = t.name ORDER BY t2.created_at, t2.id LIMIT 1) AS canon_id
        FROM tags t
      `)
    },
  },
  {
    version: 8,
    description: 'Replicated user settings (study preferences, theme) as last-writer-wins keys',
    up: async (db) => {
      await db.run(`
        CREATE TABLE IF NOT EXISTS settings (
          key        TEXT PRIMARY KEY,
          value      TEXT NOT NULL,
          updated_at TEXT NOT NULL DEFAULT ''
        ) STRICT
      `)
    },
  },
  {
    version: 9,
    description: 'Socratic exam: item bank (last-writer-wins), attempts and events (append-only)',
    up: async (db) => {
      // Items carry their Guide (rubric, hints, reference) as JSON: the bank
      // is read whole, never queried by rubric field. `status` lives in its
      // own column so approving an item is one UPDATE.
      await db.run(`
        CREATE TABLE IF NOT EXISTS socratic_items (
          id             TEXT    PRIMARY KEY,
          source_note_id TEXT,
          concept_id     TEXT    NOT NULL,
          concept_name   TEXT    NOT NULL DEFAULT '',
          prompt         TEXT    NOT NULL,
          guide          TEXT    NOT NULL,
          status         TEXT    NOT NULL,
          created_at     INTEGER NOT NULL,
          updated_at     TEXT    NOT NULL DEFAULT ''
        ) STRICT
      `)
      await db.run('CREATE INDEX IF NOT EXISTS idx_si_note ON socratic_items(source_note_id)')
      // Attempts and events never change once written: the exam state is
      // recomputed from them (lib/socratic/fold.ts), so they replicate as
      // grow-only sets. The attempt itself is JSON (lib/socratic/types.ts).
      await db.run(`
        CREATE TABLE IF NOT EXISTS socratic_attempts (
          id         TEXT    PRIMARY KEY,
          session_id TEXT    NOT NULL,
          started_at INTEGER NOT NULL,
          concept_id TEXT    NOT NULL,
          data       TEXT    NOT NULL
        ) STRICT
      `)
      await db.run(`
        CREATE TABLE IF NOT EXISTS socratic_events (
          id         TEXT PRIMARY KEY,
          attempt_id TEXT NOT NULL,
          kind       TEXT NOT NULL,
          at         TEXT NOT NULL,
          payload    TEXT NOT NULL
        ) STRICT
      `)
    },
  },
]

export const STATE_SCHEMA: SchemaDefinition = {
  baseline: STATE_DB_SCHEMA,
  baselineVersion: STATE_BASELINE_VERSION,
  migrations: STATE_MIGRATIONS,
}
